/** @jsxImportSource @emotion/react */
import { ReactNode, useRef, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';

import * as styles from './Dialog.style';
import { getBottomSafeAreaPadding } from '../../../utils/styleHelper';
import Icon from '../../Icon';
import Button from '../../Button';
import { ButtonColorType } from '../../Button/Button';

import usePreventBodyScroll from '../../../hooks/usePreventBodyScroll';
import useResetScroll from '../../../hooks/useResetScroll';
import useModalHistory from '../../../hooks/useModalHistory';
import { IS_SSR } from '../../../../../shared/constants/common';

type DialogButtonWidthResizeType = 'confirm' | 'equal';
export const DialogButtonWidthResizes: DialogButtonWidthResizeType[] = ['confirm', 'equal'];
export const DialogTypes: ButtonColorType[] = ['primary', 'secondary', 'gray', 'outline'];

export interface PropsType {
  modalKey?: string;
  className?: string;
  children: ReactNode;
  isOpen: boolean;
  toggle: (isOpen: boolean) => void;
  titleText?: string;
  buttonWidthResizeType?: 'confirm' | 'equal';
  titleOptionItem?: ReactNode;
  confirmButtonContent?: ReactNode;
  confirmButtonType?: ButtonColorType;
  optionButtonContent?: ReactNode;
  optionButtonType?: ButtonColorType;
  optionButtonClassName?: string;
  onConfirmClick?: () => void;
  onOptionClick?: () => void;
  onCloseClick?: (event: React.MouseEvent<Element, MouseEvent>) => void;
  disabled?: boolean;
  loading?: boolean;
  optionDisabled?: boolean;
  hasTitleMotion?: boolean;
}

const Dialog = forwardRef<HTMLDivElement, PropsType>(
  (
    {
      modalKey = '',
      className,
      children,
      isOpen,
      toggle,
      buttonWidthResizeType = 'confirm',
      titleText,
      titleOptionItem,
      confirmButtonContent,
      confirmButtonType = 'primary',
      onConfirmClick,
      onCloseClick,
      optionButtonContent,
      optionButtonType = 'gray',
      onOptionClick,
      disabled = false,
      loading = false,
      optionDisabled = false,
      hasTitleMotion = false,
    },
    ref
  ) => {
    const contentRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => contentRef.current as HTMLDivElement, [isOpen]);
    useResetScroll(contentRef, isOpen);
    usePreventBodyScroll(isOpen);

    function handleConfirmButtonClick() {
      onConfirmClick && onConfirmClick();
    }

    function handleOptionButtonClick() {
      onOptionClick && onOptionClick();
    }

    function handleClose() {
      toggle && toggle(false);
    }

    function handleClosableClick(event: React.MouseEvent<Element, MouseEvent>) {
      onCloseClick && onCloseClick(event);
      handleClose();
    }

    useModalHistory(modalKey, isOpen, handleClose);

    const dialog = (
      <div
        role="dialog"
        className={className}
        css={[styles.root, isOpen ? styles.show : styles.hide, getBottomSafeAreaPadding()]}
      >
        {Boolean(titleText) && (
          <div
            css={[styles.titleWrapper, hasTitleMotion && styles.titleMotion]}
            className="mrt-dialog-title"
          >
            <div css={styles.title}>
              <Icon name="ic_close_md_gray_700" alt="close" onClick={handleClosableClick} />
              <span css={styles.titleTextWrapper}>
                <span css={!titleOptionItem && styles.titleText}>{titleText}</span>
              </span>
              {titleOptionItem || null}
            </div>
          </div>
        )}
        <div
          ref={contentRef}
          className="mrt-dialog-content"
          css={styles.content(Boolean(confirmButtonContent), Boolean(titleText))}
        >
          {children}
        </div>
        {confirmButtonContent && (
          <div css={styles.footerWrapper}>
            <div css={[styles.footer, getBottomSafeAreaPadding(12)]}>
              {optionButtonContent && (
                <span
                  css={[
                    styles.optionButton,
                    buttonWidthResizeType === 'equal' && styles.equalOptionButton,
                  ]}
                >
                  <Button
                    type={optionButtonType}
                    onClick={handleOptionButtonClick}
                    disabled={optionDisabled}
                  >
                    {optionButtonContent}
                  </Button>
                </span>
              )}
              <span css={styles.confirmButton}>
                <Button
                  type={confirmButtonType}
                  onClick={handleConfirmButtonClick}
                  disabled={disabled}
                  loading={loading}
                >
                  {confirmButtonContent}
                </Button>
              </span>
            </div>
          </div>
        )}
      </div>
    );

    return IS_SSR ? dialog : createPortal(dialog, document.body);
  }
);

export default Dialog;
